import type React from 'react';
import { useState, useEffect } from 'react';
import { Star, Send, Mail, MoreVertical, MessageSquare } from 'lucide-react';
import type { Applicant } from '../../../types/applicant.js';
import { ApplicantStatus, RatingStatus } from '../../../types/enums.js';
import { StatusUpdateModal } from '../../modals/StatusUpdateModal';
import { updateApplicantStatus } from '../../../lib/api/updateApplicantStatus.js';
import { StatusDot } from '../../common/StatusDot';
import { EmailEditModal } from '../../modals/EmailEditModal.js';
import { updateGroupChatStatus } from '../../../lib/api/updateGroupChatStatus.js';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-hot-toast';
import { SendOfferToApplicantModal } from '../../modals/SendOfferToApplicantModal';

interface ScreeningTableProps {
  applicants: Applicant[];
  onViewDetails: (applicant: Applicant) => void;
  onUpdate: () => Promise<void>;
  onStatusUpdate: (applicantId: string, newStatus: ApplicantStatus) => void;
  onRatingUpdate: (applicantId: string, rating: number) => void;
  setApplicants: React.Dispatch<React.SetStateAction<Applicant[]>>;
  brandId: string;
  orgId: string;
}

// Add this type definition for filter configurations
type FilterConfig = {
  id: string;
  label: string;
  filter: (applicant: Applicant) => boolean;
};

export function ScreeningTable({ 
  applicants, 
  onViewDetails, 
  onUpdate, 
  onStatusUpdate, 
  onRatingUpdate,
  setApplicants,
  brandId,
  orgId
}: ScreeningTableProps) {
  const { getAccessTokenSilently } = useAuth0();
  const [sortField, setSortField] = useState<string>('firstName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [activeFilter, setActiveFilter] = useState<string>('unrated');
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState<Applicant | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [emailApplicant, setEmailApplicant] = useState<Applicant | null>(null);
  const [setEmailType] = useState<'sample' | 'offer'>('sample');

  // Define the filter configurations with reordered tabs
  const filterConfigs: FilterConfig[] = [
    {
      id: 'unrated',
      label: 'Unrated',
      filter: (applicant: Applicant) => 
        applicant.ratingStatus === RatingStatus.UNRATED
    },
    {
      id: 'rated',
      label: 'Rated',
      filter: (applicant: Applicant) => 
        applicant.ratingStatus === RatingStatus.RATED
    },
    {
      id: 'sample_requested',
      label: 'Sample Requested',
      filter: (applicant: Applicant) => 
        applicant.status === ApplicantStatus.SAMPLE_REQUESTED
    },
    {
      id: 'rejected',
      label: 'Rejected',
      filter: (applicant: Applicant) => 
        applicant.status === ApplicantStatus.REJECTED
    },
    {
      id: 'offer_sent',
      label: 'Offer Sent',
      filter: (applicant: Applicant) => 
        applicant.status === ApplicantStatus.OFFER_SENT
    },
    {
      id: 'all',
      label: 'All',
      filter: () => true
    }
  ];

  // Filter applicants based on active filter
  const filteredApplicants = applicants.filter(applicant => {
    // If "all" is selected, show all applicants without filtering
    if (activeFilter === 'all') return true;
    
    // For other filters, apply the filter configuration
    const activeFilterConfig = filterConfigs.find(config => config.id === activeFilter);
    return activeFilterConfig ? activeFilterConfig.filter(applicant) : true;
  });

  // Get count for each filter
  const filterCounts = filterConfigs.reduce((acc, config) => {
    // For "all" tab, show total count without filtering
    if (config.id === 'all') {
      acc[config.id] = applicants.length;
      return acc;
    }
    
    // For other tabs, apply their specific filters
    acc[config.id] = applicants.filter(config.filter).length;
    return acc;
  }, {} as Record<string, number>);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedApplicants = [...filteredApplicants].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (sortField) {
      case 'status':
        aValue = a.status || ApplicantStatus.APPLIED;
        bValue = b.status || ApplicantStatus.APPLIED;
        break;
      case 'name':
        aValue = `${a.firstName} ${a.lastName}`;
        bValue = `${b.firstName} ${b.lastName}`;
        break;
      case 'rating':
        aValue = a.rating || 0;
        bValue = b.rating || 0;
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      case 'taskStatus':
        aValue = Number(a.groupChatCreated) + 
                Number(a.sampleRequestSent) + 
                Number(a.offerSent);
        bValue = Number(b.groupChatCreated) + 
                Number(b.sampleRequestSent) + 
                Number(b.offerSent);
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      default:
        aValue = a[sortField as keyof Applicant];
        bValue = b[sortField as keyof Applicant];
    }

    return sortDirection === 'asc'
      ? String(aValue).localeCompare(String(bValue))
      : String(bValue).localeCompare(String(aValue));
  });

  // Group chat update handler
  const handleGroupChatUpdate = async (applicantId: string, currentStatus: boolean) => {
    try {
      const token = await getAccessTokenSilently();
      
      // Optimistically update UI
      const newStatus = !currentStatus;
      setApplicants(prev => prev.map(app => 
        app.id === applicantId 
          ? {
              ...app,
              groupChatCreated: newStatus
            }
          : app
      ));
      
      // Make API call
      await updateGroupChatStatus(applicantId, brandId, orgId, newStatus, token);
      
      // Show success notification
      toast.success(newStatus ? 'Added to group chat' : 'Removed from group chat');
    } catch (error) {
      console.error('Failed to update group chat status:', error);
      
      // Show error notification
      toast.error('Failed to update group chat status');
      
      // Revert optimistic update
      setApplicants(prev => prev.map(app => 
        app.id === applicantId 
          ? {
              ...app,
              groupChatCreated: currentStatus
            }
          : app
      ));
    }
  };

  // Status update handler
  const handleStatusUpdate = async (applicantId: string, newStatus: ApplicantStatus) => {
    try {
      const token = await getAccessTokenSilently();
      
      // Optimistically update UI
      onStatusUpdate(applicantId, newStatus);
      
      // Make API call
      await updateApplicantStatus(applicantId, brandId, orgId, newStatus, token);
      
      // Show success notification
      toast.success(`Status updated to ${newStatus.toLowerCase().replace(/_/g, ' ')}`);
      
      setStatusModalOpen(false);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
      await onUpdate();
    }
  };

  const SortableHeader = ({ field, children, className }: { 
    field: string, 
    children: React.ReactNode,
    className?: string 
  }) => (
    <th className={`px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider ${className || ''}`}>
      <button
        type="button"
        className="w-full flex items-center space-x-1 hover:text-gray-100"
        onClick={() => handleSort(field)}
        onKeyDown={(e) => e.key === 'Enter' && handleSort(field)}
      >
        <span>{children}</span>
        {sortField === field && (
          <span className="ml-1">
            {sortDirection === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </button>
    </th>
  );

  // Add event listener for sample request modal
  useEffect(() => {
    const handleShowSampleRequestModal = (event: CustomEvent<{ applicantId: string }>) => {
      const applicant = applicants.find(a => a.id === event.detail.applicantId);
      if (applicant) {
        setEmailApplicant(applicant);
        setEmailModalOpen(true);
      }
    };

    window.addEventListener('showSampleRequestModal', handleShowSampleRequestModal as EventListener);
    return () => {
      window.removeEventListener('showSampleRequestModal', handleShowSampleRequestModal as EventListener);
    };
  }, [applicants]);

  return (
    <div className="bg-gray-800 rounded-lg shadow-xl w-full h-[610px] flex flex-col">
      <div className="sticky top-0 z-20">
        <div className="flex space-x-1 p-2 bg-gray-900 rounded-t-lg">
          {filterConfigs.map(config => (
            <button
              type="button"
              key={config.id}
              onClick={() => setActiveFilter(config.id)}
              className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors
                ${activeFilter === config.id 
                  ? 'bg-indigo-600 text-white' 
                  : 'text-gray-300 hover:bg-gray-700'
                }`}
            >
              {config.label} ({filterCounts[config.id]})
            </button>
          ))}
        </div>
        <div className="bg-gray-900">
          <table className="w-full">
            <thead>
              <tr className="h-12">
                <SortableHeader field="status" className="w-16">Status</SortableHeader>
                <SortableHeader field="name" className="w-48">Name</SortableHeader>
                <SortableHeader field="email" className="w-48">Email</SortableHeader>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 tracking-wider w-32">Portfolio</th>
                <SortableHeader field="rating" className="w-32">Rating</SortableHeader>
                <SortableHeader field="taskStatus" className="w-32">Task Status</SortableHeader>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 tracking-wider w-16">Actions</th>
              </tr>
            </thead>
          </table>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <tbody className="bg-gray-800 divide-y divide-gray-700">
            {sortedApplicants.map((applicant) => (
              <tr
                key={applicant.id}
                className="hover:bg-gray-700 transition-colors cursor-pointer h-[60px]"
                onClick={() => onViewDetails(applicant)}
                onKeyDown={(e) => e.key === 'Enter' && onViewDetails(applicant)}
                tabIndex={0}
                aria-label={`View details for ${applicant.firstName} ${applicant.lastName}`}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <StatusDot status={applicant.status || ApplicantStatus.APPLIED} />
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-100">
                    {`${applicant.firstName} ${applicant.lastName || 'N.'}`}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-300">{applicant.email}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    type="button"
                    className="text-indigo-400 hover:text-indigo-300"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewDetails(applicant);
                    }}
                  >
                    View Details
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Star
                        key={`${applicant.id}-star-${index}`}
                        className={`w-4 h-4 cursor-pointer ${
                          applicant.rating && 
                          index < applicant.rating
                            ? 'text-yellow-400' 
                            : 'text-gray-600'
                        }`}
                        fill={applicant.rating && 
                        index < applicant.rating
                          ? 'currentColor' 
                          : 'none'}
                        onClick={(e) => {
                          e.stopPropagation();
                          const newRating = index + 1;
                          onRatingUpdate(applicant.id, newRating);
                        }}
                        onMouseEnter={(e) => {
                          const stars = e.currentTarget.parentElement?.children;
                          if (stars) {
                            for (let i = 0; i <= index; i++) {
                              stars[i].classList.add('text-yellow-400');
                              stars[i].classList.remove('text-gray-600');
                            }
                          }
                        }}
                        onMouseLeave={(e) => {
                          const stars = e.currentTarget.parentElement?.children;
                          if (stars) {
                            for (let i = 0; i < stars.length; i++) {
                              if (!applicant.rating || i >= applicant.rating) {
                                stars[i].classList.remove('text-yellow-400');
                                stars[i].classList.add('text-gray-600');
                              }
                            }
                          }
                        }}
                      />
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex space-x-2">
                    <MessageSquare 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGroupChatUpdate(applicant.id, applicant.groupChatCreated);
                      }}
                      className={`w-4 h-4 cursor-pointer hover:opacity-80 ${
                        applicant.groupChatCreated 
                          ? 'text-purple-400' 
                          : 'text-gray-500'
                      }`}
                    />
                    <Send 
                      className={`w-4 h-4 ${
                        applicant.sampleRequestSent 
                          ? 'text-blue-400' 
                          : 'text-gray-500'
                      }`}
                    />
                    <Mail 
                      className={`w-4 h-4 ${
                        applicant.offerSent 
                          ? 'text-green-400' 
                          : 'text-gray-500'
                      }`}
                    />
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium relative">
                  <div className="relative group">
                    <div className="p-4 flex justify-center hover:bg-gray-700 rounded-lg">
                      <button 
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className="text-gray-400 hover:text-gray-300"
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>

                      <div className="absolute top-1/2 right-0 bg-gray-900 rounded-md shadow-lg py-1 hidden group-hover:block z-50" 
                           style={{ minWidth: '200px' }}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedApplicant(applicant);
                            setStatusModalOpen(true);
                          }}
                          className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 w-full text-left"
                        >
                          Update Status
                        </button>
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            setEmailApplicant(applicant);
                            setEmailModalOpen(true);
                          }}
                          className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 w-full text-left"
                        >
                          Send Sample Request
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEmailApplicant(applicant);
                            setOfferModalOpen(true);
                          }}
                          className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 w-full text-left"
                        >
                          Send Offer
                        </button>
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            handleGroupChatUpdate(applicant.id, applicant.groupChatCreated);
                          }}
                          className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 w-full text-left"
                        >
                          {applicant.groupChatCreated ? 'Remove from Group Chat' : 'Create Group Chat'}
                        </button>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {statusModalOpen && selectedApplicant && (
        <StatusUpdateModal
          isOpen={statusModalOpen}
          onClose={() => setStatusModalOpen(false)}
          onUpdate={(newStatus) => handleStatusUpdate(selectedApplicant.id, newStatus)}
          currentStatus={selectedApplicant.status || ApplicantStatus.APPLIED}
        />
      )}
      <EmailEditModal
        isOpen={emailModalOpen}
        onClose={() => {
          setEmailModalOpen(false);
          setEmailApplicant(null);
        }}
        applicant={emailApplicant}
        type="sample"
        brandId={brandId}
        orgId={orgId}
        onSuccess={onUpdate}
        setApplicants={setApplicants}
      />

      <SendOfferToApplicantModal
        isOpen={offerModalOpen}
        onClose={() => {
          setOfferModalOpen(false);
          setEmailApplicant(null);
        }}
        applicant={emailApplicant}
        brandId={brandId}
        orgId={orgId}
        onSuccess={() => {
          // Optimistically update the applicant's metadata
          setApplicants(prev => prev.map(app => 
            app.id === emailApplicant?.id 
              ? {
                  ...app,
                  offerSent: true,
                  status: ApplicantStatus.OFFER_SENT
                }
              : app
          ));
          setOfferModalOpen(false);
          setEmailApplicant(null);
        }}
      />
    </div>
  );
}